// External dependencies
import { Component } from 'react';
import { connect } from 'react-redux';
import { toDate, utcToZonedTime } from 'date-fns-tz';

// CSS files
import './App.css';

// Local components
import Home from './Home';
import WorkShift from './WorkShift';
import WorkBreak from './WorkBreak';
import LightStatus from './LightStatus';
import useHassioConnection from '../hooks/useHassioConnection';
import DebugConsoleOverlay from './DebugConsoleOverlay';

// Redux thunks (slices)
import { startTimer } from '../redux/slices/timeSlice';
import { updateWeather } from '../redux/slices/weatherSlice';
import { requestAhWorkShift } from '../redux/slices/ahworkSlice';
import { getTheme } from '../redux/slices/themeSlice';
import { getTodoistTask } from '../redux/slices/todoistSlice';

class App extends Component {

  // Function to determine if the WorkShift component should be rendered
  shouldRenderWorkShift = () => {
    // Return false if work shift start_time is null or undefined
    if (!this.props.ahWorkShift.start_time) {
      return false;
    }

    // Define the Amsterdam timezone
    const amsterdamTimeZone = 'Europe/Amsterdam';

    // Get the current local time
    const currentTime = new Date();

    // Convert the work shift start time to Amsterdam timezone
    const targetTime = utcToZonedTime(toDate(this.props.ahWorkShift.start_time), amsterdamTimeZone);

    // Calculate the start of the event day (midnight)
    const startOfEventDay = new Date(targetTime);
    startOfEventDay.setHours(0, 0, 0, 0); // Set to midnight of the event day

    // Calculate when to start displaying the work shift (21:30 the day before)
    const workShiftDisplayStart = new Date(startOfEventDay);
    workShiftDisplayStart.setHours(-2, -30); // Set to 21:30 of the previous day

    // Return true if current time is between work shift display start and the event start time
    if (currentTime >= workShiftDisplayStart && currentTime < targetTime) {
      return true;
    }

    // Otherwise, return false
    return false;
  };

  // Function to determine if the WorkBreak component should be rendered
  shouldRenderWorkBreak = () => {
    // Return false if break_start_time is null or undefined
    if (!this.props.ahWorkShift.break_start_time) {
      return false;
    }

    // Define the Amsterdam timezone
    const amsterdamTimeZone = 'Europe/Amsterdam';

    // Get the current local time
    const currentTime = new Date();

    // Convert the break start time to Amsterdam timezone
    const breakStartTime = utcToZonedTime(toDate(this.props.ahWorkShift.break_start_time), amsterdamTimeZone);

    // Calculate break end time (break_start_time + 45 minutes)
    const breakEndTime = new Date(breakStartTime.getTime() + 45 * 60 * 1000);

    // Return true if current time is between break start and break end time
    return currentTime >= breakStartTime && currentTime <= breakEndTime;
  };

  // Lifecycle method that runs after the component is mounted
  componentDidMount() {
    // Initialize Home Assistant connection via hook-equivalent mount shim
    // We cannot use hooks inside class components, so we mount a small functional child below

    // Start the timer when the component mounts
    this.props.startTimer();

    // Fetch and update the weather information
    this.props.updateWeather();

    // Set an interval to refresh weather information every 30 minutes
    setInterval(this.props.updateWeather, 30 * 60 * 1000);

    // Request the work shift details
    this.props.requestAhWorkShift();

    // Set an interval to refresh work shift details every 5 minutes
    setInterval(this.props.requestAhWorkShift, 5 * 60 * 1000);

    // Fetch and apply the current theme
    this.props.getTheme();

    // Set an interval to refresh the theme every 30 minutes
    setInterval(this.props.getTheme, 30 * 60 * 1000);

    // Fetch the tasks from Todoist
    this.props.getTodoistTask();

    // Set an interval to refresh Todoist tasks every 5 minutes
    setInterval(this.props.getTodoistTask, 5 * 60 * 1000);
  }

  // Render method to conditionally display components
  render() {
    // Helper component to run the hook inside our class component tree
    const HassioBootstrap = () => { useHassioConnection(); return null; };
    // If the work break should be rendered, display the WorkBreak component
    if (this.shouldRenderWorkBreak()) {
      return <><HassioBootstrap /><WorkBreak /></>;
    }

    // If the work shift should be rendered, display the WorkShift component
    if (this.shouldRenderWorkShift()) {
      return <><HassioBootstrap /><WorkShift /></>;
    }

    // Otherwise, display the Home component
    return <>
      <HassioBootstrap />
      {/* <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
        <LightStatus />
      </div> */}
      <DebugConsoleOverlay />
      <Home />
    </>;
  }
}

// Map Redux state to the component's props
const mapStateToProps = state => ({
  now: state.time.now, // Current time from Redux state
  ahWorkShift: state.ahwork, // Work shift information from Redux state
});

// Map dispatch actions to the component's props
const mapDispatchToProps = {
  startTimer, // Action to start the timer
  updateWeather, // Action to update weather data
  requestAhWorkShift, // Action to request work shift details
  getTheme, // Action to fetch the current theme
  getTodoistTask, // Action to fetch Todoist tasks
};

// Connect the component to Redux and export it
export default connect(mapStateToProps, mapDispatchToProps)(App);
